import React, { useState } from 'react';
import { getPartnerDisplayName, getPartnerGender } from '../../services/partnershipService';
import { StatusBadge } from '../common';

/**
 * Component to display top partnerships for a player
 */
const TopPartnerships = ({ partnerships, isLoading, currentPlayerId }) => {
  const [selectedPartnership, setSelectedPartnership] = useState(null);

  if (isLoading) {
    return (
      <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold mb-4 text-text-primary">🏆 Top Partnerships</h2>
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!partnerships || partnerships.length === 0) {
    return (
      <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold mb-4 text-text-primary">🏆 Top Partnerships</h2>
        <div className="flex justify-center items-center h-32 bg-surface-subtle rounded-lg">
          <div className="text-center">
            <p className="text-text-muted mb-2">No partnerships found</p>
            <p className="text-sm text-text-muted">Start playing with other players to build partnerships!</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine chemistry score variant
  const getChemistryScoreVariant = (score) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    if (score >= 0.4) return 'warning';
    return 'danger';
  };

  // Determine win rate color (semantic tokens)
  const getWinRateColor = (rate) => {
    if (rate >= 70) return 'text-success-dark';
    if (rate >= 50) return 'text-warning-dark';
    return 'text-danger-dark';
  };

  // Format last played date
  const formatLastPlayed = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  return (
    <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
      <h2 className="text-xl font-bold mb-4 text-text-primary">🏆 Top Partnerships</h2>
      
      <div className="space-y-4">
        {partnerships.map((partnership, index) => {
          const partnerName = getPartnerDisplayName(partnership, currentPlayerId);
          const partnerGender = getPartnerGender(partnership, currentPlayerId);
          const isFemale = partnerGender?.toLowerCase() === 'female';
          
          const chemistryVariant = getChemistryScoreVariant(partnership.chemistry_score);
          const winRateColor = getWinRateColor(partnership.contextWinRate || 0);
          
          return (
            <div
              key={partnership.id}
              className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                selectedPartnership?.id === partnership.id ? 'border-primary bg-info-light' : 'border-border-medium'
              }`}
              onClick={() => setSelectedPartnership(
                selectedPartnership?.id === partnership.id ? null : partnership
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-surface-subtle rounded-full flex items-center justify-center border border-border">
                    <span className="text-sm font-bold text-text-secondary">#{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className={`font-medium ${isFemale ? 'text-female' : 'text-text-primary'}`}>
                        {partnerName}
                      </h3>
                      <span className="text-xs text-text-muted bg-surface-subtle px-2 py-1 rounded border border-border">
                        {partnership.pair_type || 'levels'}
                      </span>
                    </div>
                    <div className="text-sm text-text-secondary">
                      {partnership.contextTotalGames || 0} games • Last played {formatLastPlayed(partnership.lastPlayedAt)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <StatusBadge variant={chemistryVariant} size="small">
                      {parseFloat(partnership.chemistry_score || 0).toFixed(2)}
                    </StatusBadge>
                    <div className="text-xs text-text-muted">Chemistry</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-lg font-bold ${winRateColor}`}>
                      {(partnership.contextWinRate || 0).toFixed(0)}%
                    </div>
                    <div className="text-xs text-text-muted">Win Rate</div>
                  </div>
                  {partnership.contextAvgPerformanceVsExpected !== undefined && (
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        partnership.contextAvgPerformanceVsExpected > 0 ? 'text-success-dark' : 'text-danger-dark'
                      }`}>
                        {partnership.contextAvgPerformanceVsExpected > 0 ? '+' : ''}
                        {(partnership.contextAvgPerformanceVsExpected * 100).toFixed(1)}%
                      </div>
                      <div className="text-xs text-text-muted">vs Expected</div>
                    </div>
                  )}
                </div>
              </div>
              {selectedPartnership?.id === partnership.id && (
                <div className="mt-4 pt-4 border-t border-border-medium">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-text-muted">Total Games</div>
                      <div className="font-medium text-text-primary">{partnership.total_games || 0}</div>
                    </div>
                    <div>
                      <div className="text-text-muted">Total Wins</div>
                      <div className="font-medium text-text-primary">{partnership.total_wins || 0}</div>
                    </div>
                    <div>
                      <div className="text-text-muted">Formed</div>
                      <div className="font-medium text-text-primary">
                        {partnership.created_at ? new Date(partnership.created_at).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-surface-subtle rounded-lg border border-border">
                    <div className="text-sm">
                      <div className="font-medium text-text-primary mb-1">Performance Insights</div>
                      <div className="text-text-secondary">
                        {partnership.contextAvgPerformanceVsExpected > 0.1 ? (
                          <span className="text-success-dark">🎯 You perform significantly better than expected with {partnerName}</span>
                        ) : partnership.contextAvgPerformanceVsExpected < -0.1 ? (
                          <span className="text-danger-dark">⚠️ You perform below expectations with {partnerName}</span>
                        ) : (
                          <span className="text-text-secondary">📊 You perform as expected with {partnerName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 pt-4 border-t border-border-medium text-sm text-text-secondary">
        <div className="flex justify-between items-center">
          <span>Showing top {partnerships.length} partnerships by chemistry score</span>
          <span className="text-primary hover:text-primary-dark cursor-pointer">
            View all partnerships →
          </span>
        </div>
      </div>
    </div>
  );
};

export default TopPartnerships;
